import { useMemo } from "react";
import { useSelector } from "react-redux";
import { sessionsByProjectGroupSelector } from "../../stats/state/statsSlice";
import { getSessionDate } from "../utils/dateUtils";
import type { DateRange } from "../components/DateRangePicker";

export type RankingEntityType =
  | "parcours"
  | "modules"
  | "apprenants"
  | "groupes"
  | "groupes_parcours";

export interface RankingItem {
  id: number | string;
  name: string;
  rank: number;
  totalSessions: number;
  completedSessions: number;
  completionRate: number; // 0-100
  averageScore: number | null; // average score or null if no scores
  averageScoreMax: number | null; // average score_max or null if no scores
}

export type RankingSortKey =
  | "totalSessions"
  | "completedSessions"
  | "completionRate"
  | "averageScore";

interface UseRankingDataParams {
  dateRange: DateRange;
  entityType: RankingEntityType;
  sortKey: RankingSortKey;
  sortDirection: "asc" | "desc";
  searchValue?: string;
}

interface UseRankingDataResult {
  data: RankingItem[];
  isLoading: boolean;
  totalCount: number;
}

export const useRankingData = ({
  dateRange,
  entityType,
  sortKey,
  sortDirection,
  searchValue = "",
}: UseRankingDataParams): UseRankingDataResult => {
  const sessionsByProjectGroup = useSelector(sessionsByProjectGroupSelector);

  const rawData = useMemo(() => {
    const items: Map<number | string, RankingItem> = new Map();

    if (entityType === "parcours") {
      // Aggregate by project group
      sessionsByProjectGroup.forEach((group) => {
        let totalSessions = 0;
        let completedSessions = 0;
        let totalScore = 0;
        let totalScoreMax = 0;
        let scoreCount = 0;

        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);

            // Apply date range filter
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;

            totalSessions++;
            if (session.is_completed) {
              completedSessions++;
            }
            if (session.score !== null && session.score !== undefined) {
              totalScore += session.score;
              totalScoreMax += session.score_max ?? 0;
              scoreCount++;
            }
          });
        });

        if (totalSessions > 0) {
          items.set(group.project_group_id, {
            id: group.project_group_id,
            name: group.project_group_name,
            rank: 0,
            totalSessions,
            completedSessions,
            completionRate: Math.round((completedSessions / totalSessions) * 100),
            averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
            averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
          });
        }
      });
    } else if (entityType === "modules") {
      // Aggregate by project (module)
      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          let totalSessions = 0;
          let completedSessions = 0;
          let totalScore = 0;
          let totalScoreMax = 0;
          let scoreCount = 0;

          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);

            // Apply date range filter
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;

            totalSessions++;
            if (session.is_completed) {
              completedSessions++;
            }
            if (session.score !== null && session.score !== undefined) {
              totalScore += session.score;
              totalScoreMax += session.score_max ?? 0;
              scoreCount++;
            }
          });

          if (totalSessions > 0) {
            items.set(project.project_id, {
              id: project.project_id,
              name: project.project_name || `Module ${project.project_id}`,
              rank: 0,
              totalSessions,
              completedSessions,
              completionRate: Math.round((completedSessions / totalSessions) * 100),
              averageScore: scoreCount > 0 ? Math.round((totalScore / scoreCount) * 10) / 10 : null,
              averageScoreMax: scoreCount > 0 ? Math.round(totalScoreMax / scoreCount) : null,
            });
          }
        });
      });
    } else if (entityType === "apprenants") {
      // Aggregate by user
      const userMap = new Map<
        string,
        {
          id: string;
          name: string;
          totalSessions: number;
          completedSessions: number;
          totalScore: number;
          totalScoreMax: number;
          scoreCount: number;
        }
      >();

      sessionsByProjectGroup.forEach((group) => {
        group.projects.forEach((project) => {
          project.sessions.forEach((session) => {
            const sessionDate = getSessionDate(session);

            // Apply date range filter
            if (dateRange.startDate && sessionDate < dateRange.startDate) return;
            if (dateRange.endDate && sessionDate > dateRange.endDate) return;

            // Get user identifier
            let userId: string;
            let userName: string;

            if (session.enduser) {
              userId = `user-${session.enduser.id}`;
              userName =
                `${session.enduser.firstname || ""} ${session.enduser.lastname || ""}`.trim() ||
                session.enduser.username ||
                session.enduser.email ||
                "Anonyme";
            } else if (session.user_name) {
              userId = `name-${session.user_name}`;
              userName = session.user_name;
            } else {
              // Skip anonymous sessions without user info
              return;
            }

            const existing = userMap.get(userId);
            if (existing) {
              existing.totalSessions++;
              if (session.is_completed) {
                existing.completedSessions++;
              }
              if (session.score !== null && session.score !== undefined) {
                existing.totalScore += session.score;
                existing.totalScoreMax += session.score_max ?? 0;
                existing.scoreCount++;
              }
            } else {
              userMap.set(userId, {
                id: userId,
                name: userName,
                totalSessions: 1,
                completedSessions: session.is_completed ? 1 : 0,
                totalScore: session.score ?? 0,
                totalScoreMax: session.score_max ?? 0,
                scoreCount: session.score !== null && session.score !== undefined ? 1 : 0,
              });
            }
          });
        });
      });

      userMap.forEach((userData) => {
        items.set(userData.id, {
          id: userData.id,
          name: userData.name,
          rank: 0,
          totalSessions: userData.totalSessions,
          completedSessions: userData.completedSessions,
          completionRate: Math.round((userData.completedSessions / userData.totalSessions) * 100),
          averageScore:
            userData.scoreCount > 0
              ? Math.round((userData.totalScore / userData.scoreCount) * 10) / 10
              : null,
          averageScoreMax:
            userData.scoreCount > 0
              ? Math.round(userData.totalScoreMax / userData.scoreCount)
              : null,
        });
      });
    }

    return Array.from(items.values());
  }, [sessionsByProjectGroup, dateRange, entityType]);

  // Filter by search
  const filteredData = useMemo(() => {
    if (!searchValue) return rawData;
    const search = searchValue.toLowerCase();
    return rawData.filter((item) => item.name.toLowerCase().includes(search));
  }, [rawData, searchValue]);

  // Sort and assign ranks
  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case "totalSessions":
          comparison = b.totalSessions - a.totalSessions;
          break;
        case "completedSessions":
          comparison = b.completedSessions - a.completedSessions;
          break;
        case "completionRate":
          comparison = b.completionRate - a.completionRate;
          break;
        case "averageScore":
          // Null scores go to the end
          if (a.averageScore === null && b.averageScore === null) comparison = 0;
          else if (a.averageScore === null) comparison = 1;
          else if (b.averageScore === null) comparison = -1;
          else comparison = b.averageScore - a.averageScore;
          break;
      }

      return sortDirection === "asc" ? -comparison : comparison;
    });

    // Assign ranks (handling ties)
    let currentRank = 1;
    let previousValue: number | null = null;
    let sameRankCount = 0;

    return sorted.map((item, index) => {
      const currentValue =
        sortKey === "averageScore" ? item.averageScore : item[sortKey as keyof RankingItem];

      if (index === 0) {
        previousValue = currentValue as number | null;
        return { ...item, rank: 1 };
      }

      if (currentValue === previousValue) {
        sameRankCount++;
        return { ...item, rank: currentRank };
      } else {
        currentRank = currentRank + sameRankCount + 1;
        sameRankCount = 0;
        previousValue = currentValue as number | null;
        return { ...item, rank: currentRank };
      }
    });
  }, [filteredData, sortKey, sortDirection]);

  return {
    data: sortedData,
    isLoading: sessionsByProjectGroup.length === 0,
    totalCount: sortedData.length,
  };
};
